import logger from '../../services/logging/logging-service';
import { satelliteTracker } from '../../services/tracking/satellite-tracking-service';
import { forEach } from '../../utils/array-utils';
import { getInfiniteData } from '../../utils/client-requests';
import { changeUrlNoHistory } from '../../utils/change-url';
import { INFINITE_SCROLLING as INFINITE_SCROLLING_CLASSNAME } from '../../utils/csr-anchors';
import { trackComscore } from '../../utils/analytics';

import { infiniteCardTemplate, loaderTemplate } from './infinite-scroll-templates';

const baseUrl = window.location.origin + window.location.pathname;

const MAX_PAGES = 10;
const INFINITE_SCROLLING_CONTAINER_CLASSNAME = INFINITE_SCROLLING_CLASSNAME;
const PAGE_COUNTER_CLASSNAME = 'j-page-counter';

const INFINITE_SCROLLING_TRACKING_CONSTANT = 'infinite_scrolling';
const infiniteScrollURLCalled = [];

/**
 * Each page corresponds to a new set of artciles
 */
let pageCount = 1;

/**
 * The sentinel that will trigger the infinite scroll
 */
const sentinel = document.querySelector('#sentinel') || null;

/**
 * Tracking analytics
 */
const trackingInfiniteScrolling = () => {
  satelliteTracker.trackInWeb(INFINITE_SCROLLING_TRACKING_CONSTANT);
};

/**
 * THe node that will contain the infinite scolling new articles
 */
const infiniteContainer = document.querySelector(INFINITE_SCROLLING_CONTAINER_CLASSNAME)
  ? document.querySelector(INFINITE_SCROLLING_CONTAINER_CLASSNAME)
  : null;

const staticLastChild = infiniteContainer !== null ? infiniteContainer.lastElementChild : null;
if (staticLastChild !== null) {
  staticLastChild.classList.add(PAGE_COUNTER_CLASSNAME);
  staticLastChild.dataset.page = 1;
}
/**
 * Configurations for the infinite scrolling observer
 */
const infinityOptions = {
  rootMargin: '0px 0px 300px 0px',
  threshold: [0.1],
};
/**
 * configurations for the url changer observer
 */
const paginationOptions = {
  threshold: [0.1],
};

/**
 * @param {*} callback function that is executed whenever an element they wish to monitor enters or exits another element (or the viewport)
 * @param {*} options The options object passed into the IntersectionObserver() constructor let you control the circumstances under which the observer's callback is invoked.
 */
const infinityObserver = (callback, options = infinityOptions) => new IntersectionObserver(callback, options);

/**
 * SEO Metadata Tags
 */
const addMetaLinkElement = (type = 'next', href = '?pag=2') => {
  const link = document.createElement('link');
  link.rel = type;
  link.href = href;
  document.getElementsByTagName('head')[0].appendChild(link);
};

const findMetalinkElementByRelType = (relType = 'next') => {
  const head = document.getElementsByTagName('head')[0];
  const linksInHead = head.getElementsByTagName('link');
  let linkFounded;
  forEach((link) => {
    if (link.rel === relType) {
      linkFounded = link;
    }
  })(linksInHead);
  return linkFounded;
};

const removeMetaLink = (relType) => {
  const link = findMetalinkElementByRelType(relType);
  if (link) {
    link.parentNode.removeChild(link);
  }
};

const updateLinkMeta = (type, href) => {
  const linkMeta = findMetalinkElementByRelType(type);
  if (linkMeta) {
    linkMeta.setAttribute('href', href);
  } else {
    addMetaLinkElement(type, href);
  }
};

const updateLinksMetadataOnPageIntersecting = (pageNumber) => {
  const hrefPrev =
    pageNumber === 2
      ? `${window.location.origin}${window.location.pathname}`
      : `${window.location.origin}${window.location.pathname}?pag=${pageNumber - 1}`;
  updateLinkMeta('prev', hrefPrev);
  if (pageNumber >= MAX_PAGES) {
    removeMetaLink('next');
  } else {
    const hrefNext = `${window.location.origin}${window.location.pathname}?pag=${pageNumber + 1}`;
    updateLinkMeta('next', hrefNext);
  }
};

const updateLinksMetadataOnFirstPage = () => {
  removeMetaLink('prev');
  const hrefNext = `${window.location.origin}${window.location.pathname}?pag=2`;
  updateLinkMeta('next', hrefNext);
};

/**
 * Observer for the url change in the infinite scrolling pages
 */
const paginationObserver = new IntersectionObserver((entry) => {
  const params = new URLSearchParams(document.location.search.substring(1)); // skips the '?'
  const previousPageNumber = params.get('pag') !== '' ? Number(params.get('pag')) : null;

  if (entry[0].isIntersecting && entry[0].target.dataset.page > 1) {
    updateLinksMetadataOnPageIntersecting(Number(entry[0].target.dataset.page));
    changeUrlNoHistory(`?pag=${entry[0].target.dataset.page}`);
    const currentPageNumber = Number(entry[0].target.dataset.page);
    if (previousPageNumber !== currentPageNumber) {
      trackingInfiniteScrolling();
    }
  } else if (entry[0].isIntersecting && Number(entry[0].target.dataset.page) === 1) {
    updateLinksMetadataOnFirstPage();
    changeUrlNoHistory(baseUrl);
  }
}, paginationOptions);

/**
 * A function that will controll the loader waiting for the fetch of the infinite scrolling data
 * @param {*} boolean a boolean that will tell to the function if the loader should appear or not
 * @param {*} parent The parent of the inifinite scolling's card, the loader will be appender after it
 */
const isLoading = (boolean, parent) => {
  let loader = document.querySelector('#infinite-loader') || null;
  if (boolean === true) {
    if (loader === null) {
      const loaderContainer = document.createElement('div');
      loaderContainer.innerHTML = loaderTemplate();
      parent.insertAdjacentElement('afterend', loaderContainer);
      loader = document.querySelector('#infinite-loader');
    } else if (loader) {
      loader.style.visibility = 'visible';
    }
  } else {
    loader.style.visibility = 'hidden';
  }
};

/**
 * A function that will update the path to call to get the json conaining the new articles
 * @param {*} div the dom element to update
 * @param {*} newUrl the new url
 */
const updateInfinitePath = (div, newUrl) => {
  const divToUpdate = div;
  divToUpdate.dataset.pathPage = newUrl;
};

/**
 * A function that will render the new articles
 * @param {html element} el The parent of the article cards
 * @param {JSON} list a json containing the data of the new articles
 */
const infiniteTemplateRender = (el, list) => {
  const articleList = list.cardList;
  const setOfCardsClasses = list.style.cardContainerCssClass;
  const singleCardClasses = `c-card--base ${list.cardMapping}`;
  const singleCardBlogClasses = `${list.blogCardMapping} c-card--blog`;
  if (articleList.length > 0 && list.nextPagePath) {
    pageCount = pageCount + 1;
    let templateToAppend = '';
    articleList.forEach((card, i) => {
      const cardClasses = card[0].blog ? singleCardBlogClasses : singleCardClasses;
      const pagination = {};
      if (i === 0) {
        pagination.isFirst = true;
        pagination.counter = pageCount;
      } else if (i === articleList.length - 1) {
        pagination.isLast = true;
        pagination.counter = pageCount;
      } else {
        pagination.isFirst = false;
      }
      templateToAppend = templateToAppend + infiniteCardTemplate(card[0], setOfCardsClasses, cardClasses, pagination);
    });
    el.innerHTML += templateToAppend;
    articleList.length < 20 ? (el.dataset.noData = true) : updateInfinitePath(el, list.nextPagePath);
  } else {
    el.dataset.noData = true;
  }
};

/**
 * The infinite scrolling function
 */
export const init = ($containerDiv) => {
  if (infiniteContainer !== null && sentinel !== null) {
    const sentinelObserver = infinityObserver(async (entry) => {
      if (entry[0].isIntersecting && !infiniteContainer.dataset.noData) {
        const dataURL = $containerDiv.dataset.pathPage;
        if (dataURL && !infiniteScrollURLCalled.includes(dataURL)) {
          trackComscore();
          infiniteScrollURLCalled.push(dataURL);
          isLoading(true, infiniteContainer);
          const data = await getInfiniteData(dataURL);
          isLoading(false, infiniteContainer);
          infiniteTemplateRender(infiniteContainer, data);
          const paginators = document.querySelectorAll(`.${PAGE_COUNTER_CLASSNAME}`);
          paginators.forEach((element) => paginationObserver.observe(element));
        }
      } else if (infiniteContainer.dataset.noData) {
        sentinelObserver.disconnect();
      }
    });
    sentinelObserver.observe(sentinel);
  } else {
    logger.warn('Error in infinite scrolling');
  }
};
